import { useCallback, useEffect, useRef } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useAuth, useUser } from "@/app/providers";
import { ME, UPDATE_CURRENT_USER_PROFILE } from "@/hooks";
import { fillUserWithMe } from "@/utils";
import { useAppDispatch } from "@/app/store";
import { updateUser } from "@/features/auth";

export function useMe(
  onCompleted: (data?: unknown) => void = (): void => {},
  onError: (error?: unknown) => void = (): void => {},
) {
  const dispatch = useAppDispatch();
  const { setUser, setIsLoadingUser } = useUser();
  const lastUserRef = useRef(null);
  const { unsetToken } = useAuth();
  const apolloClient = useApolloClient();

  const [comUpdateAccount] = useMutation<any>(UPDATE_CURRENT_USER_PROFILE);

  const updateProfile = useCallback(
    async (variables: Record<string, unknown>) => {
      try {
        await comUpdateAccount({
          variables,
          refetchQueries: [{ query: ME }],
        });
      } catch {
        // Silently fail
      }
    },
    [comUpdateAccount],
  );

  const { data, loading, error, refetch } = useQuery<any>(ME, {
    errorPolicy: "all",
  });

  // Handle successful query
  useEffect(() => {
    if (data?.me) {
      // Set initial user immediately so dependent dropdowns and queries start right away
      if (!lastUserRef.current) {
        lastUserRef.current = data.me;
        setUser(data.me);
      }
      fillUserWithMe(data.me, apolloClient, setUser, updateProfile)
        .then((finalMe) => {
          // Prevent infinite loop: only update if user actually changed
          const prev = lastUserRef.current;
          if (JSON.stringify(prev) !== JSON.stringify(finalMe)) {
            dispatch(updateUser(finalMe));
            lastUserRef.current = finalMe;
            setUser(finalMe);
            setIsLoadingUser(false);
            (onCompleted as any)(finalMe);
          } else {
            setIsLoadingUser(false);
          }
        })
        .catch((_e) => {
          // Still set user even if fillUserWithMe fails
          const prev = lastUserRef.current;
          if (JSON.stringify(prev) !== JSON.stringify(data.me)) {
            lastUserRef.current = data.me;
            dispatch(updateUser(data.me));
            setUser(data.me);
            (onCompleted as any)(data.me);
          }
          setIsLoadingUser(false);
        });
    }
  }, [
    data,
    setUser,
    setIsLoadingUser,
    onCompleted,
    apolloClient,
    updateProfile,
    dispatch,
  ]);

  // Handle errors
  useEffect(() => {
    if (error) {
      switch (error.message) {
        case "Signature verification failed":
        case "Invalid authorization token. Please provide a valid token.":
        case "Invalid header padding":
          unsetToken();
          break;
        default:
          break;
      }
      (onError as any)(error);
    }
  }, [error, onError, unsetToken]);

  return {
    refetchMe: refetch,
    user: data?.me,
    loading,
  };
}
